import apiClient from './api/client'
import type {
  TalentProfileAnonymized,
  TalentProfileFull,
  TalentProfileListItem,
  Skill,
  ProfileOption,
  Language,
  Country,
  ExpertiseOffering,
  Currency,
  TimezoneResponse,
  Institution,
  AcademicDegree,
} from '../types/talent'
import type { PaginatedResponse } from '../types'

export const talentService = {
  // Get paginated list of talents
  async getTalents(params?: {
    page?: number
    skills?: string[]
    availability?: string
    min_rate?: number
    max_rate?: number
    country?: string
  }): Promise<PaginatedResponse<TalentProfileListItem>> {
    const { data } = await apiClient.get('/talents/profiles/', { params })
    return data
  },

  // Get single talent profile (anonymized or full based on connection)
  async getTalent(id: string): Promise<TalentProfileAnonymized | TalentProfileFull> {
    const { data } = await apiClient.get(`/talents/profiles/${id}/`)
    return data
  },

  // Get current user's talent profile
  async getMyProfile(): Promise<TalentProfileFull> {
    const { data } = await apiClient.get('/talents/profiles/me/')
    return data
  },

  // Update current user's talent profile
  async updateMyProfile(profileData: Partial<TalentProfileFull>): Promise<TalentProfileFull> {
    const { data } = await apiClient.patch('/talents/profiles/me/', profileData)
    return data
  },

  // Get all skills
  async getSkills(search?: string, category?: string): Promise<Skill[]> {
    const { data } = await apiClient.get('/talents/skills/', { params: { search, category } })
    return data
  },

  // Add skill to profile
  async addSkill(skillId: string, proficiency?: string, yearsExperience?: number): Promise<void> {
    await apiClient.post('/talents/profiles/add_skill/', {
      skill_id: skillId,
      proficiency,
      years_experience: yearsExperience,
    })
  },

  // Remove skill from profile
  async removeSkill(skillId: string): Promise<void> {
    await apiClient.delete('/talents/profiles/remove_skill/', {
      data: { skill_id: skillId },
    })
  },

  // Get profile options by category
  async getProfileOptions(category?: string): Promise<ProfileOption[]> {
    const { data } = await apiClient.get('/talents/options/', { params: { category } })
    return data
  },

  // Get all active languages
  async getLanguages(): Promise<Language[]> {
    const { data } = await apiClient.get('/talents/languages/')
    return data
  },

  // Get all active countries
  async getCountries(): Promise<Country[]> {
    const { data } = await apiClient.get('/talents/countries/')
    return data
  },

  // Get expertise offerings with nested sub-options
  async getExpertiseOfferings(): Promise<ExpertiseOffering[]> {
    const { data } = await apiClient.get('/talents/expertise-offerings/')
    return data
  },

  // Get all active currencies
  async getCurrencies(): Promise<Currency[]> {
    const { data } = await apiClient.get('/talents/currencies/')
    return data
  },

  // Get IANA timezones
  async getTimezones(): Promise<TimezoneResponse> {
    const { data } = await apiClient.get('/talents/timezones/')
    return data
  },

  // Create a new skill (get-or-create via slug)
  async createSkill(name: string, category?: string): Promise<Skill> {
    const { data } = await apiClient.post('/talents/skills/', { name, category })
    return data
  },

  // Create a new profile option (get-or-create via slug)
  async createProfileOption(name: string, category: string, order?: number): Promise<ProfileOption> {
    const { data } = await apiClient.post('/talents/options/', { name, category, ...(order !== undefined && { order }) })
    return data
  },

  // Get institutions (searchable)
  async getInstitutions(search?: string): Promise<Institution[]> {
    const { data } = await apiClient.get('/talents/institutions/', { params: { search } })
    return data
  },

  // Create a new institution (get-or-create via slug)
  async createInstitution(name: string): Promise<Institution> {
    const { data } = await apiClient.post('/talents/institutions/', { name })
    return data
  },

  // Get academic degrees for current user's profile
  async getAcademicDegrees(): Promise<AcademicDegree[]> {
    const { data } = await apiClient.get('/talents/academic-degrees/')
    return data
  },

  // Create academic degree
  async createAcademicDegree(degreeData: {
    level: string
    field_of_study?: string
    institution_ids?: number[]
    order?: number
  }): Promise<AcademicDegree> {
    const { data } = await apiClient.post('/talents/academic-degrees/', degreeData)
    return data
  },

  // Update academic degree
  async updateAcademicDegree(
    id: string,
    degreeData: {
      level?: string
      field_of_study?: string
      institution_ids?: number[]
      order?: number
    },
  ): Promise<AcademicDegree> {
    const { data } = await apiClient.patch(`/talents/academic-degrees/${id}/`, degreeData)
    return data
  },

  // Delete academic degree
  async deleteAcademicDegree(id: string): Promise<void> {
    await apiClient.delete(`/talents/academic-degrees/${id}/`)
  },
}

export default talentService
